import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import submit from "submit-form";
import useClickOutside from "../../hooks/useClickOutside";
import { useRillContext } from "../../rill/RillReact/share/base";
import getAvatarUrl from "../../utils/get-avatar-url";
import { canViewAllOrganizationClients } from "../../utils/organization-permissions-helpers";
import { getCoachFromAllAthletes } from "../../utils/user-helpers";
import SvgIcon from "../SvgIcon";

export const AthleteSelect = ({
  athleteSwitch = false,
  athleteList,
  handleAthleteChange,
  name, //the input name if an input with the athlete id is needed
  initialAthlete,
  classNames,
}) => {
  const { t } = useTranslation("coaching-portal");
  const { locals } = useRillContext();
  const { activeUser, organizationRoleName, allAthletesFromOrganization } =
    locals || {};
  const referAsClients = activeUser.referAsClients || false;
  const theme = activeUser.webAppTheme || "dark";

  //The user is allowed to see al the athletes of the coaches
  const isAllowToViewAllOrganizationsCoaches =
    canViewAllOrganizationClients(organizationRoleName);

  const athletes = useMemo(() => {
    if (athleteList) return sortAthletes(athleteList);
    if (isAllowToViewAllOrganizationsCoaches && allAthletesFromOrganization) {
      return sortAthletes(allAthletesFromOrganization);
    }
    if (activeUser?.athletes) return sortAthletes(activeUser.athletes);
    return [];
  }, [
    athleteList,
    isAllowToViewAllOrganizationsCoaches,
    allAthletesFromOrganization,
    activeUser?.athletes,
  ]);

  const inputRef = useRef(null);
  const dropdownRef = useRef(null);
  const filterDropdownRef = useRef(null);
  const filterToggleRef = useRef(null);
  const [searchAthlete, setSearchAthlete] = useState("");
  const [filteredList, setFilteredList] = useState([]);
  const [searchOpened, setSearchOpened] = useState(false);
  const [filterMode, setFilterMode] = useState("active");
  const [filterDropdownOpen, setFilterDropdownOpen] = useState(false);
  const [filterDropdownPosition, setFilterDropdownPosition] = useState({
    top: 0,
    right: 0,
  });
  const [athleteData, setAthleteData] = useState({
    name: "",
    imgSrc: "",
    id: "",
  });
  useClickOutside(dropdownRef, () => setSearchOpened(false), searchOpened);
  useClickOutside(
    filterDropdownRef,
    () => setFilterDropdownOpen(false),
    filterDropdownOpen
  );

  // Load filter mode from localStorage on mount
  useEffect(() => {
    if (typeof window !== "undefined" && window.localStorage) {
      const savedFilterMode = localStorage.getItem("athleteSelectFilterMode");
      if (
        savedFilterMode &&
        ["all", "active", "inactive"].includes(savedFilterMode)
      ) {
        setFilterMode(savedFilterMode);
      }
    }
  }, []);

  // Update filtered list when search or filter mode changes
  useEffect(() => {
    const filteredBySearch =
      searchAthlete.length > 0
        ? filterAthletes(searchAthlete, athletes)
        : athletes;

    // Apply status filter
    const filteredByStatus = filterByStatus(filteredBySearch, filterMode);
    setFilteredList(filteredByStatus);
  }, [searchAthlete, filterMode, athletes]);
  useEffect(() => {
    if (locals.query.athlete) return setSelectedAthlete(locals.query.athlete);
    if (initialAthlete) return setSelectedAthlete(initialAthlete);
  }, [locals.query.athlete, initialAthlete]);
  const [currentOptionIndex, setCurrentOptionIndex] = useState(0);

  const setSelectedAthlete = (athleteId) => {
    const athlete = athletes.find(
      (athlete) => athlete._id === athleteId || athlete.athleteId === athleteId
    );

    setAthleteData({
      name: athlete?.name || athlete?.athleteName || "",
      imgSrc: getAvatarUrl(athlete, null, theme),
      id: athleteId,
    });
  };
  function filterAthletes(searchString, athletes) {
    const filteredAthletes = athletes.filter((athlete) => {
      const nameVariable = athlete.name ? "name" : "athleteName";
      return athlete[nameVariable]
        .toLowerCase()
        .includes(searchString.trim().toLowerCase());
    });
    return filteredAthletes;
  }

  function filterByStatus(athletes, mode) {
    if (mode === "all") return athletes;

    return athletes.filter((athlete) => {
      // status can be athlete.status or athlete.athleteStatus (depends if using athletes array or allAthletesFromOrganization array)
      const isInactive =
        athlete.status?.inactive || athlete.athleteStatus?.inactive || false;

      if (mode === "active") {
        return !isInactive;
      }
      if (mode === "inactive") {
        return isInactive;
      }
      return true;
    });
  }

  const handleFilterChange = (newMode) => {
    setFilterMode(newMode);
    setFilterDropdownOpen(false);

    if (typeof window !== "undefined" && window.localStorage) {
      localStorage.setItem("athleteSelectFilterMode", newMode);
    }
  };

  const toggleFilterDropdown = (e) => {
    e.stopPropagation();

    if (!filterDropdownOpen && filterToggleRef.current) {
      const rect = filterToggleRef.current.getBoundingClientRect();
      setFilterDropdownPosition({
        top: rect.bottom,
        left: window.innerWidth - rect.left,
      });
    }

    setFilterDropdownOpen(!filterDropdownOpen);
  };
  const updateAthlete = (id) => {
    if (handleAthleteChange) {
      setSelectedAthlete(id);
      setSearchOpened(false);
      return handleAthleteChange(id);
    }
    const { isFromOtherOrganizationMember, organizationCoachId } =
      getCoachFromAllAthletes(activeUser, id, allAthletesFromOrganization);
    let body = { athlete: id, athleteSwitch };
    if (isFromOtherOrganizationMember) {
      body.organizationCoach = organizationCoachId;
    } else {
      body.organizationCoach = "";
    }
    if (locals.pathname.includes("metrics/check-in-summary")) {
      body = { ...body, checkInDate: "", checkInFrequency: "" };
    }
    submit(`/actions/refresh`, {
      method: "GET",
      body,
    });
    setSearchAthlete("");
    setSearchOpened(false);
  };
  const handleClick = (e) => {
    e.preventDefault();
    if (!searchOpened) {
      setSearchOpened(true);
      focusInput();
    }
  };
  const focusInput = () => {
    setTimeout(() => {
      inputRef?.current?.focus();
    }, 500);
  };
  const handleKeyPress = (event) => {
    const { key } = event;

    if (searchOpened) {
      switch (key) {
        case "Escape":
          setSearchOpened(false);
          setSearchAthlete("");
          break;
        case "ArrowDown":
          moveFocusDown();
          break;
        case "ArrowUp":
          moveFocusUp();
          break;
        case "Enter": {
          const athleteId = filteredList[currentOptionIndex]?.athleteId;
          if (athleteId) {
            setSelectedAthlete(athleteId);
            updateAthlete(athleteId);
          }
          break;
        }
        default:
          break;
      }
    }
  };

  const moveFocusDown = () => {
    if (currentOptionIndex < filteredList.length - 1) {
      setCurrentOptionIndex(currentOptionIndex + 1);
    } else {
      setCurrentOptionIndex(0);
    }
  };

  const moveFocusUp = () => {
    if (currentOptionIndex > 0) {
      setCurrentOptionIndex(currentOptionIndex - 1);
    } else {
      setCurrentOptionIndex(filteredList.length - 1);
    }
  };
  return (
    <div className={classNames}>
      {name && <input name={name} type="hidden" value={athleteData.id} />}
      <details role="list" open className="dropdown-img" ref={dropdownRef}>
        <summary onClick={handleClick}>
          {athleteData.imgSrc.length > 0 && <img src={athleteData.imgSrc} />}
          {searchOpened ? (
            <input
              ref={inputRef}
              value={searchAthlete}
              onChange={(e) => setSearchAthlete(e.target.value)}
              onKeyDown={(e) => {
                if (["Enter", "Tab"].includes(e.key)) {
                  e.preventDefault();
                  e.stopPropagation();
                }
                handleKeyPress(e);
              }}
            />
          ) : (
            <p
              className={
                athleteData.name
                  ? undefined
                  : "text-(--white-30) flex items-center gap-2 no-athlete-selected"
              }
            >
              {athleteData.name ||
                (referAsClients
                  ? t("athleteSelect.searchClient")
                  : t("athleteSelect.searchAthlete"))}
            </p>
          )}
          <SvgIcon
            src={
              searchOpened ? "/img/svg/angle-up.svg" : "/img/svg/angle-down.svg"
            }
          />
        </summary>
        <ul role="listbox" className={searchOpened ? "opened" : ""}>
          <li className="athlete-list-header">
            <span className="athlete-list-title">
              {referAsClients
                ? t("athleteSelect.clientList")
                : t("athleteSelect.athleteList")}
            </span>
            <div className="filter-dropdown" ref={filterDropdownRef}>
              <button
                type="button"
                className="filter-toggle"
                ref={filterToggleRef}
                onClick={toggleFilterDropdown}
              >
                {filterMode === "all" && t("athleteSelect.all")}
                {filterMode === "active" && t("athleteSelect.activeOnly")}
                {filterMode === "inactive" && t("athleteSelect.inactiveOnly")}
                <SvgIcon
                  src={
                    filterDropdownOpen
                      ? "/img/svg/angle-up.svg"
                      : "/img/svg/angle-down.svg"
                  }
                />
              </button>
              {filterDropdownOpen && (
                <div
                  className="filter-dropdown-menu"
                  style={{
                    top: `${filterDropdownPosition.top}px`,
                    right: `${filterDropdownPosition.right}px`,
                  }}
                >
                  <label className="filter-option">
                    <input
                      type="radio"
                      name="athlete-filter"
                      checked={filterMode === "all"}
                      onChange={() => handleFilterChange("all")}
                    />
                    <span>{t("athleteSelect.all")}</span>
                  </label>
                  <label className="filter-option">
                    <input
                      type="radio"
                      name="athlete-filter"
                      checked={filterMode === "active"}
                      onChange={() => handleFilterChange("active")}
                    />
                    <span>{t("athleteSelect.activeOnlyLabel")}</span>
                  </label>
                  <label className="filter-option">
                    <input
                      type="radio"
                      name="athlete-filter"
                      checked={filterMode === "inactive"}
                      onChange={() => handleFilterChange("inactive")}
                    />
                    <span>{t("athleteSelect.inactiveOnlyLabel")}</span>
                  </label>
                </div>
              )}
            </div>
          </li>
          {filteredList.map((athlete, i) => (
            <li
              key={(athlete._id || athlete.athleteId) + i}
              onClick={() => updateAthlete(athlete._id || athlete.athleteId)}
              role="option"
              className={currentOptionIndex === i ? "current" : ""}
            >
              <img src={getAvatarUrl(athlete, _, theme)} />
              <span>{athlete.name || athlete.athleteName}</span>
            </li>
          ))}
          {filteredList.length === 0 && searchAthlete.length > 0 && (
            <li>{t("athleteSelect.noResultsFound")}</li>
          )}
        </ul>
      </details>
    </div>
  );
};

const sortAthletes = (athletes) => {
  if (!athletes) return [];

  // Create a copy to avoid mutating the original array
  return [...athletes].sort((athleteA, athleteB) => {
    const nameVariable = athleteA.name ? "name" : "athleteName";
    const nameA = athleteA[nameVariable].toUpperCase(); // ignore upper and lowercase
    const nameB = athleteB[nameVariable].toUpperCase(); // ignore upper and lowercase
    if (nameA < nameB) {
      return -1;
    }
    if (nameA > nameB) {
      return 1;
    }
    return 0; // names must be equal
  });
};
